// Public /tutorial-demo page: an animated, narrated walkthrough of the product, from login
// to automated posting. The screens are the app's real widgets with dummy data
// (DemoScreens.tsx); the timeline is demoScript.ts and playback is useDemoEngine.
// Nothing here talks to the API. `?reel` renders the vertical short version instead.
import React, {useEffect, useRef, useState} from 'react'
import clsx from 'clsx'
import {SCENES, STAGE_W, STAGE_H} from './demoScript'
import {LoginScreen, AppScreen} from './DemoScreens'
import {useDemoEngine} from './useDemoEngine'
import {ReelDemo} from './ReelDemo'
import '../groups/index.scss'
import '../../components/OnboardingWizard.scss'
import '../../components/SupportWidget/SupportWidget.scss'
import './TutorialDemo.scss'

const fmt = (ms: number) => {
  const sec = Math.floor(ms / 1000)
  return `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`
}

// Page setup for the demo routes. They render outside the <App> wrapper, so the boot
// splash is never auto-hidden. And the mock screens are built for the light theme — a
// visitor whose OS is in dark mode would otherwise get Metronic's dark cards and modals
// around hard-coded light widgets — so force light while the page is open.
const useDemoPage = () =>
  useEffect(() => {
    const splash = document.getElementById('splash-screen')
    if (splash) splash.style.display = 'none'
    const root = document.documentElement
    const prevTheme = root.getAttribute('data-bs-theme')
    root.setAttribute('data-bs-theme', 'light')
    return () => {
      if (prevTheme) root.setAttribute('data-bs-theme', prevTheme)
    }
  }, [])

// onFinished: called once the walkthrough reaches its closing card (used by the
// first-login /welcome-tutorial gate).
// embed: just the player, without the page's top bar and title (the /landing video dialog);
// onRegister then replaces the closing card's sign-up link.
type Props = {onFinished?: () => void; embed?: boolean; onRegister?: () => void}

const TutorialDemo: React.FC<Props> = (props) => {
  useDemoPage()
  const reel = new URLSearchParams(window.location.search).has('reel')
  return reel ? <ReelDemo /> : <TutorialPlayer {...props} />
}

const TutorialPlayer: React.FC<Props> = ({onFinished, embed, onRegister}) => {
  const {
    t, total, index, local, scene, state, sceneStart,
    playing, started, setStarted, muted, setMuted,
    play, toggle, seek,
    stageRef, contentRef, cursorRef, rippleRef, spotRef,
  } = useDemoEngine(SCENES, STAGE_W, STAGE_H)
  const [scale, setScale] = useState(1)
  const playerRef = useRef<HTMLDivElement>(null)
  const frameRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (state.outro) onFinished?.()
  }, [state.outro, onFinished])

  // ── Fit the 1280×800 stage to the available width ──
  useEffect(() => {
    const el = frameRef.current
    if (!el) return
    const ro = new ResizeObserver(() => setScale(el.clientWidth / STAGE_W))
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        e.preventDefault()
        toggle()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [toggle])

  const fullscreen = () => {
    const el = playerRef.current
    if (!el) return
    if (document.fullscreenElement) document.exitFullscreen()
    else el.requestFullscreen?.()
  }

  // Caption: light up the words already spoken, proportional to the clip's progress.
  const words = scene.text.split(' ')
  const spoken = Math.floor(Math.min(1, local / scene.audioMs) * words.length)

  return (
    <div className={clsx('td-page', embed && 'td-embed')} dir='rtl'>
      {!embed && (
        <>
          <div className='td-topbar'>
            <img src='/media/logos/d-code-logo-light.svg' alt='FB Zipper' />
            <a href='/auth' className='td-topbar-link'>כניסה למערכת</a>
          </div>

          <div className='td-hero'>
            <h1>איך עובדים עם FB Zipper</h1>
            <p>מדריך קצר עם קריינות: מההתחברות ועד פרסום אוטומטי בקבוצות</p>
          </div>
        </>
      )}

      <div className='td-player' ref={playerRef}>
        <div className='td-frame' ref={frameRef} style={{height: STAGE_H * scale}} dir='ltr'>
          <div className='td-stage' ref={stageRef} style={{width: STAGE_W, height: STAGE_H, transform: `scale(${scale})`}}>
            {state.screen === 'login' ? <LoginScreen s={state} /> : <AppScreen s={state} contentRef={contentRef} />}

            <div className='td-spot' ref={spotRef} />
            <div className='td-ripple' ref={rippleRef} />
            <div className='td-cursor' ref={cursorRef}>
              <svg width='28' height='28' viewBox='0 0 24 24'>
                <path d='M4 2l15 11.2-6.6 1.1 3.9 7.3-2.9 1.5-3.9-7.4L4 20.5z' fill='#111' stroke='#fff' strokeWidth='1.4' strokeLinejoin='round' />
              </svg>
            </div>

            {state.outro && (
              <div className='td-outro' dir='rtl'>
                <div className='td-outro-card'>
                  <div style={{fontSize: 56}}>🚀</div>
                  <h2>זהו, אתם מוכנים לפרסם!</h2>
                  <p>קמפיין, פוסט, קבוצות ופרופיל מחובר — והמערכת עושה את השאר.</p>
                  <div className='td-outro-btns'>
                    <button className='btn btn-light' onClick={() => { seek(0); play() }}>
                      <i className='bi bi-arrow-counterclockwise me-1'></i> צפייה חוזרת
                    </button>
                    {onRegister ? (
                      <button className='btn btn-primary' onClick={onRegister}>להרשמה</button>
                    ) : (
                      <a className='btn btn-primary' href='/auth/registration'>להרשמה</a>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {!started && (
            <button className='td-poster' onClick={play} dir='rtl' style={{backgroundImage: 'linear-gradient(135deg, rgba(15, 22, 41, 0.82), rgba(24, 119, 242, 0.55)), url(/media/misc/auth-bg.png)'}}>
              <span className='td-poster-play'>
                <svg viewBox='0 0 24 24' width='44' height='44'><path d='M8 5v14l11-7z' fill='#fff' /></svg>
              </span>
              <span className='td-poster-title'>צפו במדריך</span>
              <span className='td-poster-sub'>{fmt(total)} · עם קריינות</span>
            </button>
          )}
          {started && !playing && !state.outro && (
            <button className='td-paused' onClick={play} aria-label='המשך'>
              <span className='td-poster-play small'>
                <svg viewBox='0 0 24 24' width='34' height='34'><path d='M8 5v14l11-7z' fill='#fff' /></svg>
              </span>
            </button>
          )}
        </div>

        <div className='td-caption'>
          <span className='td-caption-chip'>{index + 1}/{SCENES.length} · {scene.title}</span>
          <p>
            {words.map((w, i) => (
              <span key={i} className={clsx(started && i < spoken && 'spoken')}>{w} </span>
            ))}
          </p>
        </div>

        <div className='td-controls' dir='ltr'>
          <button className='td-ctl' onClick={toggle} aria-label={playing ? 'השהה' : 'נגן'}>
            <i className={`bi ${playing ? 'bi-pause-fill' : 'bi-play-fill'}`}></i>
          </button>
          <span className='td-time'>{fmt(t)} / {fmt(total)}</span>
          <div
            className='td-progress'
            onClick={(e) => {
              const r = e.currentTarget.getBoundingClientRect()
              // The bar runs right-to-left (row-reverse), like the page.
              seek(((r.right - e.clientX) / r.width) * total)
              setStarted(true)
            }}
          >
            {SCENES.map((s, i) => {
              const start = sceneStart(i)
              const fill = Math.max(0, Math.min(1, (t - start) / s.durationMs))
              return (
                <div key={s.id} className='td-seg' style={{flexGrow: s.durationMs}} title={s.title}>
                  <div className='td-seg-fill' style={{width: `${fill * 100}%`}} />
                </div>
              )
            })}
          </div>
          <button className='td-ctl' onClick={() => setMuted((m) => !m)} aria-label='השתק'>
            <i className={`bi ${muted ? 'bi-volume-mute-fill' : 'bi-volume-up-fill'}`}></i>
          </button>
          <button className='td-ctl' onClick={fullscreen} aria-label='מסך מלא'>
            <i className='bi bi-arrows-fullscreen'></i>
          </button>
        </div>

        <div className='td-chapters'>
          {SCENES.map((s, i) => (
            <button
              key={s.id}
              className={clsx('td-chapter', i === index && 'active', i < index && 'done')}
              onClick={() => {
                seek(sceneStart(i))
                play()
              }}
            >
              <span className='td-chapter-num'>{i + 1}</span>
              {s.title}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}

export {TutorialDemo}
